import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { z } from "zod";
import { AppShell } from "@/components/layouts/AppShell";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ApiError } from "@/lib/api";
import { forgotPassword } from "@/lib/authClient";

const searchSchema = z.object({
  email: z.string().optional(),
});

export const Route = createFileRoute("/forgot-password")({
  validateSearch: searchSchema,
  component: ForgotPasswordPage,
});

function ForgotPasswordPage() {
  const navigate = useNavigate();
  const search = Route.useSearch();

  const [email, setEmail] = useState(search.email ?? "");
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setErrorMsg(null);
    setSubmitting(true);
    try {
      const emailLc = email.trim().toLowerCase();
      await forgotPassword({ email: emailLc });
      void navigate({
        to: "/reset-password" as never,
        search: { email: emailLc } as never,
        replace: true,
      });
    } catch (e) {
      if (e instanceof ApiError) {
        setErrorMsg(e.problem.detail ?? "Could not start password reset.");
      } else {
        setErrorMsg("Could not start password reset.");
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AppShell>
      <section className="container py-16 md:py-20">
        <Card className="mx-auto max-w-md">
          <CardHeader>
            <CardTitle className="font-serif text-3xl text-palm">Reset your password</CardTitle>
            <CardDescription>
              We'll email you a 6-digit code to reset the password on your account.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={onSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  disabled={submitting}
                />
              </div>

              {errorMsg && (
                <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                  {errorMsg}
                </div>
              )}

              <Button type="submit" size="lg" className="w-full" variant="accent" disabled={submitting}>
                {submitting ? "Sending…" : "Email me a code"}
              </Button>

              <p className="text-center text-sm text-muted-foreground">
                Remembered it?{" "}
                <Link to={"/login" as never} className="text-palm underline underline-offset-2">
                  Back to sign in
                </Link>
              </p>
            </form>
          </CardContent>
        </Card>
      </section>
    </AppShell>
  );
}
